import React, { useState, useEffect } from 'react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import { X, Eye, EyeOff, LockKeyhole } from 'lucide-react';

const AccountEditModal = ({ accountId, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        username: '',
        accountName: '',
        email: '',
        phone: ''
    });
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    
    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const res = await adminStaffService.getAccountDetail(accountId);
                const data = res.data || res;
                setFormData({
                    username: data.username || '',
                    accountName: data.accountName || data.fullName || '',
                    email: data.email || '',
                    phone: data.phone || ''
                });
            } catch (error) {
                toast.error("Lỗi tải chi tiết tài khoản");
                onClose();
            }
        };
        if (accountId) fetchDetail();
        
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [accountId, onClose]); 

    const handleUpdateInfo = async (e) => {
        e.preventDefault();
        e.stopPropagation(); 
        
        try {
            await adminStaffService.updateAccount(accountId, formData);
            toast.success("Cập nhật thông tin thành công!");
            onSuccess();
        } catch (error) {
            toast.error("Lỗi cập nhật thông tin!");
        }
    };

    const handleResetPassword = (e) => {
        if(e) e.stopPropagation();

        if (!newPassword || newPassword.length < 6) {
            toast.warning("Mật khẩu mới phải có ít nhất 6 ký tự");
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error("Mật khẩu xác nhận không khớp!");
            return;
        }

        if (window.confirm("Bạn có chắc chắn muốn đặt lại mật khẩu cho tài khoản này?")) {
            adminStaffService.resetAccountPassword(accountId, newPassword)
                .then(() => {
                    toast.success("Đặt lại mật khẩu thành công!");
                    setNewPassword('');
                    setConfirmPassword('');
                })
                .catch(() => toast.error("Lỗi khi đổi mật khẩu!"));
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] animate-in fade-in duration-200"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden">
                {/* 👉 ĐÃ SỬA MÀU: bg-tnc-orange */}
                <div className="absolute top-0 left-0 w-full h-1 bg-tnc-orange"></div>
                
                <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors">
                    <X size={24} />
                </button>
                
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Quản lý tài khoản</h2>

                {/* Form sửa thông tin */}
                <form onSubmit={handleUpdateInfo} className="space-y-4 border-b border-gray-100 pb-6 mb-6">
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Username</label>
                            <input 
                                type="text" 
                                value={formData.username} 
                                disabled 
                                className="w-full mt-1 p-2.5 border rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed font-medium"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Họ và Tên</label>
                            {/* 👉 ĐÃ SỬA MÀU: focus:ring-tnc-orange/20 focus:border-tnc-orange */}
                            <input 
                                type="text" 
                                value={formData.accountName} 
                                onChange={(e) => setFormData({...formData, accountName: e.target.value})}
                                className="w-full mt-1 p-2.5 border rounded-lg focus:ring-2 focus:ring-tnc-orange/20 focus:border-tnc-orange outline-none transition-all"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email</label>
                                {/* 👉 ĐÃ SỬA MÀU: focus:ring-tnc-orange/20 focus:border-tnc-orange */}
                                <input 
                                    type="email" 
                                    value={formData.email} 
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full mt-1 p-2.5 border rounded-lg focus:ring-2 focus:ring-tnc-orange/20 focus:border-tnc-orange outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Số điện thoại</label>
                                {/* 👉 ĐÃ SỬA MÀU: focus:ring-tnc-orange/20 focus:border-tnc-orange */}
                                <input 
                                    type="text" 
                                    value={formData.phone} 
                                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                    className="w-full mt-1 p-2.5 border rounded-lg focus:ring-2 focus:ring-tnc-orange/20 focus:border-tnc-orange outline-none transition-all"
                                />
                            </div>
                        </div>
                    </div>
                    <button type="submit" className="w-full bg-[#002855] text-white py-3 rounded-lg font-bold hover:bg-blue-900 transition-all shadow-lg active:scale-[0.98]">
                        Cập nhật thông tin
                    </button>
                </form>

                {/* Khu vực Đổi mật khẩu */}
                <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        {/* 👉 ĐÃ SỬA MÀU: text-tnc-orange */}
                        <LockKeyhole size={16} className="text-tnc-orange" /> Thiết lập mật khẩu mới
                    </h3>
                    
                    <div className="space-y-3">
                        <div className="relative">
                            <input 
                                type={showNewPass ? "text" : "password"} 
                                placeholder="Mật khẩu mới (ít nhất 6 ký tự)" 
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password" 
                                className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none pr-10 transition-all text-sm"
                            />
                            <button 
                                type="button"
                                onClick={() => setShowNewPass(!showNewPass)}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                            >
                                {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        <div className="relative">
                            <input 
                                type={showConfirmPass ? "text" : "password"} 
                                placeholder="Xác nhận mật khẩu mới" 
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password" 
                                className={`w-full p-2.5 border rounded-lg outline-none pr-10 transition-all text-sm ${
                                    confirmPassword && newPassword !== confirmPassword 
                                    ? 'border-red-500 focus:ring-red-500/20' 
                                    : 'focus:ring-blue-500/20 focus:border-blue-500'
                                }`}
                            />
                            <button 
                                type="button"
                                onClick={() => setShowConfirmPass(!showConfirmPass)}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        {/* 👉 ĐÃ SỬA MÀU: hover:bg-tnc-orange */}
                        <button 
                            type="button" 
                            onClick={handleResetPassword}
                            disabled={!newPassword || newPassword !== confirmPassword}
                            className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-lg font-bold hover:bg-tnc-orange hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Đổi mật khẩu
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountEditModal;